import { FILTER_KEYS } from './reconciliationHealth'

const EMPTY_MESSAGES = {
  [FILTER_KEYS.NEEDS_RECEIPTING]:
    'No orders need receipting in this period — nice work! Try widening your date range if you expected to see more.',
  [FILTER_KEYS.NEEDS_ACTION]:
    'No orders need receipting in this period — nice work! Try widening your date range if you expected to see more.',
  [FILTER_KEYS.NEEDS_RECONCILING]:
    'No orders need invoice reconcile in this period. Receipted POs here already have a closed invoice (or none left open).',
  [FILTER_KEYS.COMPLETE]: 'No receipted orders in this period.',
  [FILTER_KEYS.RECONCILED]:
    'No invoice-reconciled orders in this period. Finish Match & reconcile (or Sent to Xero) to see them here.',
  [FILTER_KEYS.AWAITING_SUPPLIER]:
    'No orders awaiting supplier confirmation in this period.',
  [FILTER_KEYS.NO_INVOICE]: 'No orders needing an invoice in this period.',
  [FILTER_KEYS.NOT_SAVED]:
    'No orders with an attached invoice awaiting save in this period.',
  [FILTER_KEYS.CREDIT_WORKFLOW]: 'No orders in credit workflow in this period.',
  [FILTER_KEYS.VOIDED]: 'No voided orders in this date range.',
  [FILTER_KEYS.ALL]: 'No orders in this date range. Try widening your dates.',
}

/**
 * Empty table copy. When Ollie deep-links a PO that is already done (or not in
 * this filter), say so instead of only “nothing needs receipting”.
 *
 * @param {string} [filterKey]
 * @param {{ searchQuery?: string|null }} [options]
 */
export function getReconciliationEmptyMessage(
  filterKey = FILTER_KEYS.ALL,
  { searchQuery = null } = {},
) {
  const query = String(searchQuery || '').trim()
  if (query) {
    if (
      filterKey === FILTER_KEYS.NEEDS_RECEIPTING ||
      filterKey === FILTER_KEYS.NEEDS_ACTION
    ) {
      return `Nothing matching “${query}” still needs receipting — it may already be completed. Clear the search or widen the filter to look again.`
    }
    if (filterKey === FILTER_KEYS.NEEDS_RECONCILING) {
      return `Nothing matching “${query}” still needs invoice reconcile — it may already be closed. Clear the search or widen the filter to look again.`
    }
  }
  return EMPTY_MESSAGES[filterKey] || EMPTY_MESSAGES[FILTER_KEYS.ALL]
}

function receiptingDayKey(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return localDayKey(value)
  }
  const text = String(value ?? '').trim()
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})/)
  if (iso) return iso[1]
  const uk = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (uk) return `${uk[3]}-${uk[2]}-${uk[1]}`
  const ms = Number(text)
  if (Number.isFinite(ms) && Math.abs(ms) > 1e11) {
    return localDayKey(new Date(ms))
  }
  return ''
}

function localDayKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatReceiptingDay(dayKey) {
  const [year, month, date] = dayKey.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * View order lands on the current Goods Receipting range. When the purchase
 * order's date is outside that range, say so.
 */
export function describePurchaseOrderOutsideReceiptingDates({
  purchaseOrderNumber,
  orderDate,
  start,
  end,
  dateBasis = 'orderedDate',
} = {}) {
  const po = String(purchaseOrderNumber || '').trim()
  const day = receiptingDayKey(orderDate)
  const from = receiptingDayKey(start)
  const to = receiptingDayKey(end)
  if (!po || !day || !from || !to) return ''
  if (day >= from && day <= to) return ''
  const when =
    dateBasis === 'actualDeliveryDate'
      ? `delivered on ${formatReceiptingDay(day)}`
      : `ordered on ${formatReceiptingDay(day)}`
  return `${po} is outside the current Goods Receipting dates. It was ${when}.`
}

function dayKeyToMidday(dayKey) {
  const [year, month, date] = dayKey.split('-').map(Number)
  return Date.UTC(year, month - 1, date, 12)
}

/**
 * Dates that keep the current range and stretch it to include the order day.
 * Null when the order is already inside the range or a date is missing.
 */
export function receiptingRangeIncludingOrder({ orderDate, start, end } = {}) {
  const day = receiptingDayKey(orderDate)
  const from = receiptingDayKey(start)
  const to = receiptingDayKey(end)
  if (!day || !from || !to) return null
  if (day >= from && day <= to) return null
  const nextFrom = day < from ? day : from
  const nextTo = day > to ? day : to
  return {
    start: dayKeyToMidday(nextFrom),
    end: dayKeyToMidday(nextTo),
  }
}
